// Contrat des travaux et chantiers (M14, lot L4-C, D-146). Projet (numéro PRJ, D-149) avec objet,
// localisation, financement par sources et lignes budgétaires, contrats liés (module achats) ; jalons
// pondérés dont la somme fait 100 ; avancement physique = somme des pondérations des jalons validés par
// le responsable technique, ou pourcentage saisi avec justification ; avancement financier = payé sur
// montant contractuel, calculé sur les mandats payés des liquidations rattachées au projet (contrat
// commun depense.ts) ; situations de travaux = liquidations marquées « situation » ; retard automatique
// sur jalon dépassé (tâche de fond) ; rapports avec photos ; réception provisoire avec réserves, levée,
// réception définitive, clôture, procès-verbaux PDF. Les deux avancements ne sont jamais confondus.
import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const texte = (max: number, min = 1) => z.string().trim().min(min).max(max);
const facultatif = (max: number) => z.string().trim().max(max).optional();
const pieces = z.array(uuid).max(20).optional();
const limite = z.coerce.number().int().min(1).max(500).default(200);

export const ETATS_PROJET = {
  preparation: 'En préparation',
  en_cours: 'En cours',
  reception_provisoire: 'Réception provisoire prononcée',
  reception_definitive: 'Réception définitive prononcée',
  clos: 'Clos',
} as const;
export type EtatProjet = keyof typeof ETATS_PROJET;
export const CODES_ETATS_PROJET = Object.keys(ETATS_PROJET) as [EtatProjet, ...EtatProjet[]];

export const ETATS_JALON = { a_venir: 'À venir', atteint: 'Déclaré atteint', valide: 'Validé' } as const;
export type EtatJalon = keyof typeof ETATS_JALON;

export const SOURCES_FINANCEMENT = { budget_communal: 'Budget de la collectivité', etat: 'État', bailleur: 'Bailleur ou partenaire', autre: 'Autre source' } as const;
export type SourceFinancement = keyof typeof SOURCES_FINANCEMENT;
export const CODES_SOURCES_FINANCEMENT = Object.keys(SOURCES_FINANCEMENT) as [SourceFinancement, ...SourceFinancement[]];

export const TYPES_RECEPTION = { provisoire: 'Réception provisoire', definitive: 'Réception définitive' } as const;
export type TypeReception = keyof typeof TYPES_RECEPTION;

export const TYPE_NUMERO_PROJET = 'PRJ';

// Calculs purs (mêmes règles que le serveur) ---------------------------------------------------------

/** Somme des pondérations, arrondie au centième. */
export function sommePonderations(jalons: { ponderation: number }[]): number {
  return Math.round(jalons.reduce((s, j) => s + j.ponderation, 0) * 100) / 100;
}

/** Les pondérations d'un projet font exactement 100 (au centième près). */
export function ponderationsValides(jalons: { ponderation: number }[]): boolean {
  return jalons.length > 0 && jalons.every((j) => j.ponderation > 0) && Math.abs(sommePonderations(jalons) - 100) < 0.005;
}

export interface AvancementPhysique {
  /** Pourcentage de 0 à 100, une décimale. */
  taux: number;
  mode: 'jalons' | 'saisi';
  justification: string | null;
  jalons_valides: number;
  jalons: number;
}

/** Avancement physique : pourcentage saisi avec justification s'il existe, sinon pondérations validées. */
export function avancementPhysique(jalons: { ponderation: number; etat: EtatJalon }[], saisi: { taux: number; justification: string } | null = null): AvancementPhysique {
  const valides = jalons.filter((j) => j.etat === 'valide');
  const base = { jalons_valides: valides.length, jalons: jalons.length };
  if (saisi) return { ...base, taux: Math.round(saisi.taux * 10) / 10, mode: 'saisi', justification: saisi.justification };
  return { ...base, taux: Math.round(sommePonderations(valides) * 10) / 10, mode: 'jalons', justification: null };
}

export interface AvancementFinancier {
  /** Payé : mandats payés des liquidations rattachées au projet (francs CFA). */
  paye: number;
  /** Liquidé et non encore payé (information, hors du taux). */
  liquide: number;
  /** Montant contractuel : contrats liés, avenants compris. */
  montant_contractuel: number;
  /** payé / montant contractuel en pourcentage, une décimale ; null sans contrat. */
  taux: number | null;
}

export function avancementFinancier(paye: number, montantContractuel: number, liquide = 0): AvancementFinancier {
  const taux = montantContractuel > 0 ? Math.round((1000 * paye) / montantContractuel) / 10 : null;
  return { paye, liquide, montant_contractuel: montantContractuel, taux };
}

/** Un jalon est en retard quand son échéance est passée (strictement avant la date du jour) sans validation. */
export function jalonEnRetard(j: { echeance: string; etat: EtatJalon }, aujourdhui: string): boolean {
  return j.etat !== 'valide' && j.echeance < aujourdhui;
}

/** Retard d'un jalon en jours calendaires (0 s'il n'est pas en retard). */
export function joursDeRetard(j: { echeance: string; etat: EtatJalon }, aujourdhui: string): number {
  if (!jalonEnRetard(j, aujourdhui)) return 0;
  return Math.round((Date.parse(`${aujourdhui}T00:00:00Z`) - Date.parse(`${j.echeance}T00:00:00Z`)) / 86_400_000);
}

// Écritures ----------------------------------------------------------------------------------------

export const schemaCreationProjet = z
  .strictObject({
    intitule: texte(200, 3),
    objet: texte(4000, 3),
    adresse: facultatif(300),
    /** Localisation [longitude, latitude] (WGS 84), facultative. */
    position: z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]).optional(),
    service_id: uuid.optional(),
    debut_prevu: jour,
    fin_prevue: jour,
  })
  .refine((v) => v.debut_prevu <= v.fin_prevue, { message: 'La fin prévue suit le début', path: ['fin_prevue'] });
export type CreationProjet = z.infer<typeof schemaCreationProjet>;

export const schemaCreationFinancement = z.strictObject({
  source: z.enum(CODES_SOURCES_FINANCEMENT),
  libelle: texte(200, 2),
  montant: z.number().int().positive().max(1_000_000_000_000),
  /** Ligne budgétaire de la collectivité (budget communal), facultative. */
  ligne_id: uuid.optional(),
  engagement_id: uuid.optional(),
});
export type CreationFinancement = z.infer<typeof schemaCreationFinancement>;

export const schemaJalonSaisi = z.strictObject({
  libelle: texte(200, 2),
  ponderation: z.number().positive('Pondération strictement positive').max(100),
  echeance: jour,
});
/** Définition complète des jalons (remplace la liste tant qu'aucun n'est validé) : somme 100. */
export const schemaDefinitionJalons = z
  .strictObject({ jalons: z.array(schemaJalonSaisi).min(1).max(30) })
  .refine((v) => ponderationsValides(v.jalons), { message: 'La somme des pondérations doit faire 100', path: ['jalons'] });
export type DefinitionJalons = z.infer<typeof schemaDefinitionJalons>;

export const schemaActeJalon = z.strictObject({ observation: facultatif(2000) });
export const schemaAvancementSaisi = z.strictObject({
  taux: z.number().min(0).max(100),
  justification: texte(2000, 10),
});
export type AvancementSaisi = z.infer<typeof schemaAvancementSaisi>;

export const schemaLiaisonContrat = z.strictObject({ contrat_id: uuid });

/** Situation de travaux : liquidation marquée « situation » du contrat commun (D-146). */
export const schemaCreationSituation = z.strictObject({
  engagement_id: uuid,
  contrat_id: uuid.optional(),
  periode_debut: jour,
  periode_fin: jour,
  montant: z.number().int().positive().max(1_000_000_000_000),
  numero_fournisseur: texte(60),
  date_facture: jour,
  observation: facultatif(2000),
});
export type CreationSituation = z.infer<typeof schemaCreationSituation>;

export const schemaCreationRapport = z.strictObject({
  date: jour,
  contenu: texte(6000, 3),
  effectif: z.number().int().min(0).max(10_000).optional(),
  jalon_id: uuid.optional(),
  photo_ids: pieces,
});
export type CreationRapport = z.infer<typeof schemaCreationRapport>;

export const schemaCreationReception = z.strictObject({
  date: jour,
  observations: facultatif(4000),
  reserves: z.array(z.strictObject({ description: texte(1000, 3), echeance: jour.optional() })).max(50).default([]),
});
export type CreationReception = z.input<typeof schemaCreationReception>;

export const schemaLeveeReserve = z.strictObject({ observation: texte(2000, 3) });
export const schemaClotureProjet = z.strictObject({ observation: facultatif(2000) });

export const schemaFiltreProjets = z.strictObject({ etat: z.enum(CODES_ETATS_PROJET).optional(), en_retard: z.enum(['oui', 'non']).optional(), limite });

// Réponses -----------------------------------------------------------------------------------------

export interface Jalon {
  id: string;
  rang: number;
  libelle: string;
  ponderation: number;
  echeance: string;
  etat: EtatJalon;
  en_retard: boolean;
  retard_constate_le: string | null;
  atteint_le: string | null;
  valide: { par: string | null; le: string } | null;
}

export interface FinancementProjet {
  id: string;
  source: SourceFinancement;
  libelle: string;
  montant: number;
  ligne: { id: string; code: string; libelle: string } | null;
  engagement_id: string | null;
}

export interface SituationTravaux {
  liquidation_id: string;
  numero: string;
  rang: number | null;
  periode: { debut: string; fin: string } | null;
  contrat: { id: string; numero: string } | null;
  montant: number;
  liquidee_le: string;
  mandat: { numero: string; etat: string } | null;
  payee: boolean;
}

export interface RapportChantier {
  id: string;
  date: string;
  contenu: string;
  effectif: number | null;
  jalon: { id: string; libelle: string } | null;
  photos: { id: string; titre: string }[];
  auteur: string | null;
}

export interface Reserve {
  id: string;
  description: string;
  echeance: string | null;
  etat: 'ouverte' | 'levee';
  levee: { le: string; par: string | null; observation: string } | null;
}

export interface Reception {
  id: string;
  type: TypeReception;
  date: string;
  observations: string | null;
  document_id: string | null;
  prononcee_par: string | null;
  reserves: Reserve[];
}

export interface ProjetTravaux {
  id: string;
  numero: string;
  intitule: string;
  objet: string;
  adresse: string | null;
  position: [number, number] | null;
  service: { id: string; nom: string } | null;
  debut_prevu: string;
  fin_prevue: string;
  etat: EtatProjet;
  en_retard: boolean;
  /** Donnée de présentation : montants affichés « (fictif) ». */
  fictif: boolean;
  financements: FinancementProjet[];
  total_financement: number;
  contrats: { id: string; numero: string; objet: string; fournisseur: string; montant_actuel: number; etat: string }[];
  jalons: Jalon[];
  avancement_physique: AvancementPhysique;
  avancement_financier: AvancementFinancier;
  situations: SituationTravaux[];
  rapports: RapportChantier[];
  receptions: Reception[];
  cloture: { le: string; observation: string | null } | null;
}

export type ResumeProjet = Pick<ProjetTravaux, 'id' | 'numero' | 'intitule' | 'adresse' | 'position' | 'etat' | 'en_retard' | 'fictif' | 'debut_prevu' | 'fin_prevue' | 'avancement_physique' | 'avancement_financier'>;
